"use client"
import React, { useCallback, useMemo } from "react"
import { nativeBr } from "@/utils/nativeBr"
import { usePopupStore } from "@/components/common/popup/store/usePopupStore"
import { useBottomSheetStore } from "@/components/common/bottomSheet/store/useBottomSheetStore"
import fetchClientBookShelfAnalyze, {
  AnalyzeResponse,
} from "@/app/(tabs)/bookshelf/_fetch/fetchClientBookShelfAnalyze"
import { useAppVersionStore } from "@/hooks/common/store/useAppVersionStore"
import * as E from "fp-ts/Either"
import { useBookAnalysisStore } from "@/app/(tabs)/bookshelf/_store/useBookAnalysisStore"
import BookshelfAnalysisProcessingPopup from "@/app/(tabs)/bookshelf/_components/Analysis/BookshelfAnalysisProcessingPopup"
import PopupError from "@/components/common/popup/components/PopupError"

export const useBookshelfPhotoAnalysis = () => {
  const { openPopup, closePopup } = usePopupStore()
  const { closeSheet } = useBottomSheetStore()
  const { setCurrentAnalysis, setIsAnalyzing, setAnalysisError } =
    useBookAnalysisStore()
  const { version: appVer, osType: appOsType } = useAppVersionStore()

  const analysisInProgressPopup = useMemo(
    () => (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
        <div className="absolute left-1/2 top-1/2 flex h-[183px] w-[285px] -translate-x-1/2 -translate-y-1/2 flex-col items-start gap-6 rounded-lg bg-white px-4 pb-4 pt-8">
          <p className="flex h-[63px] w-[253px] items-center justify-center text-center text-[15px] font-normal leading-[142.9%] text-[#555555]">
            아직 등록이 끝나지 않은 책장이 있어요.
            <br />
            진행 중인 책장 등록을 완료하신 후
            <br />
            새로운 책장 촬영이 가능해요.
          </p>

          <div className="flex h-12 w-[253px] flex-col items-start p-0">
            <button
              onClick={closePopup}
              className="flex h-12 w-[253px] flex-col items-center justify-center gap-[10px] rounded-lg bg-[#0077FF] px-7 py-4"
            >
              <div className="flex h-[21px] w-[26px] flex-row items-center justify-center p-0">
                <span className="h-[21px] w-[26px] text-[15px] font-medium leading-[142.9%] text-white">
                  확인
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    ),
    [closePopup],
  )

  // 에러 핸들링 함수
  const handleAnalysisError = useCallback(
    (error: unknown, errorMessage: string) => {
      console.error("책장 분석 에러:", error)
      setAnalysisError(errorMessage)
      setIsAnalyzing(false)
      openPopup(
        <PopupError
          // message={"오류가 발생했습니다.\n잠시 후 다시 시도해주세요."}
          message={
            "오류가 발생했습니다.\n잠시 후 다시 시도해주세요.\n" + errorMessage
          }
        />,
      )
    },
    [setAnalysisError, setIsAnalyzing, openPopup],
  )

  const handleBookshelfPhotoAnalysis = useCallback(async () => {
    console.log(`🚀 ~ 책장 촬영으로 등록하기`)

    // [Refactor] Global store에서 앱 버전 정보 사용
    const appVersionInfo = appVer && appOsType ? { version: appVer, osType: appOsType } : null

    if (appVersionInfo) {
      console.log(`[AppVersion] Using cached version:`, appVersionInfo)
    } else {
      console.warn(`[AppVersion] Cache miss or not initialized yet`)
    }

    try {
      const res = await nativeBr.shelfPhoto()
      console.log("====shelfPhoto 응답==== ", res)

      if (!res.text) {
        // res.text가 null이거나 false일 때 - 이미 진행 중인 분석이 있는 경우
        openPopup(analysisInProgressPopup)
        return
      }

      if (res.text) {
        try {
          console.log("실제 네이티브 응답으로 API 호출 진행")
          setIsAnalyzing(true)
          setAnalysisError(null)

          const analyzeResult = await fetchClientBookShelfAnalyze({
            text: res.text,
            osType: res.osType,
            appVersion: appVersionInfo,
          })

          E.fold(
            (error) => {
              handleAnalysisError(error, "책장 분석 중 오류가 발생했습니다.")
            },
            (data: AnalyzeResponse) => {
              console.log("책장 분석 결과:", data)
              setCurrentAnalysis(data)
              setIsAnalyzing(false)

              openPopup(
                <BookshelfAnalysisProcessingPopup
                  onConfirm={() => {
                    // shelfPhotoAnalyzeId 호출 후 처리
                    nativeBr
                      .shelfPhotoAnalyzeId({ analysisId: data.id })
                      .then(() => {
                        console.log(
                          "[ShelfAnalysisHandler] shelfPhotoAnalyzeId 호출 완료",
                        )
                      })

                    // 분석 진행 안내 팝업 먼저 표시
                    closeSheet()
                    closePopup()
                  }}
                />,
              )
            },
          )(analyzeResult)
        } catch (error) {
          handleAnalysisError(error, "네트워크 오류가 발생했습니다.")
        }
      }
    } catch (error) {
      console.error("책장 분석 중 오류가 발생했습니다.\n" + error)
    }
  }, [
    openPopup,
    closePopup,
    closeSheet,
    setCurrentAnalysis,
    setIsAnalyzing,
    setAnalysisError,
    analysisInProgressPopup,
    handleAnalysisError,
    appVer,
    appOsType,
  ])

  return {
    handleBookshelfPhotoAnalysis,
  }
}
